import { useMemo, useState } from 'react';
import { Alert, Button, Card, Group, Skeleton, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconDeviceTv, IconSparkles } from '@tabler/icons-react';
import {
  BRAND_KIT_COPY as C,
  BRAND_KIT_GENERATE_ERRORS,
  type FactoryAssetPlatform,
  type TvPlatformKey,
} from '@/lib/constants/appFactory';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryBrandKitGenerateReq } from '@/lib/types/AppFactory';
import { ApiError } from '@/shared/api/ApiError';
import { BrandKitUtils, type BrandKitLiveKind, type BrandKitScopeData } from '../BrandKitUtils';
import { useFactoryAssetSets, useGenerateBrandKit } from '../useAppFactory';
import { BrandKitDrafts } from './BrandKitDrafts';
import { BrandKitForm } from './BrandKitForm';

interface AppBrandKitTabProps {
  appCfgId: number;
  app: AppCfgsDto;
  isAppActive: boolean;
  /** The TV platforms that are on for the app and that the account is entitled to. */
  tv: readonly TvPlatformKey[];
}

/**
 * The app's Brand Kit (3.42.0; jvm notes/ott/tv-graphics/
 * design_brand_kit_app_level_20260930.md): the brand inputs once, every TV
 * platform's images rendered from them. This tab holds the form, the drafts
 * of every platform with one Approve all, and the one-click render for a
 * platform turned on after the kit existed. Each platform tab's Images keeps
 * its current images, its TV screens and the per-file uploads. Everything
 * names only the TV platforms that are on for the app.
 */
export function AppBrandKitTab({ appCfgId, app, isAppActive, tv }: AppBrandKitTabProps) {
  const scopes = useMemo(() => BrandKitUtils.enabledScopes(tv), [tv]);
  const rokuOn = scopes.includes('roku');
  const firetvOn = scopes.includes('firetv');
  const rokuQuery = useFactoryAssetSets(appCfgId, 'roku', rokuOn);
  const firetvQuery = useFactoryAssetSets(appCfgId, 'firetv', firetvOn);
  const generateMutation = useGenerateBrandKit(appCfgId);
  // The platform ticks live here, not in the form: a Generate stores a new kit
  // and the form remounts on it — what the customer ticked must survive that,
  // or "Generate again" would silently drop a platform and leave its older
  // draft waiting beside the new ones. null = not chosen yet (the defaults).
  const [chosenTicks, setChosenTicks] = useState<FactoryAssetPlatform[] | null>(null);

  if (scopes.length === 0) {
    return (
      <Card withBorder p="xl" radius="md" data-testid="brand-kit-no-platform">
        <Stack align="center" gap="sm">
          <IconDeviceTv size={40} color="var(--mantine-color-gray-5)" />
          <Text fw={600} size="lg">
            {C.noPlatformTitle}
          </Text>
          <Text c="dimmed" ta="center" maw={440}>
            {C.noPlatform}
          </Text>
        </Stack>
      </Card>
    );
  }

  const queries = [rokuOn ? rokuQuery : null, firetvOn ? firetvQuery : null].filter((q) => q != null);
  // Only a read that has NOTHING to show takes the tab down: a failed re-read
  // (every generate and decision re-reads both platforms) keeps the last
  // answer on screen, so the form, its picked files and the other platform's
  // drafts are not thrown away by one 502.
  if (queries.some((q) => q.isError && !q.data)) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        {C.unavailable}
      </Alert>
    );
  }
  if (queries.some((q) => !q.data)) {
    return (
      <Stack gap="lg">
        <Skeleton height={80} radius="md" />
        <Skeleton height={320} radius="md" />
      </Stack>
    );
  }

  const data: Partial<Record<FactoryAssetPlatform, BrandKitScopeData>> = {};
  if (rokuOn && rokuQuery.data) data.roku = rokuQuery.data;
  if (firetvOn && firetvQuery.data) data.firetv = firetvQuery.data;
  // Every platform's read carries the same app-level kit; the first that has one speaks.
  const kit = queries.map((q) => q.data?.brandKit).find((k) => k != null) ?? null;
  const seed = kit ? null : (queries.map((q) => q.data?.brandKitSeed).find((s) => s != null) ?? null);
  const liveKinds: Partial<Record<FactoryAssetPlatform, BrandKitLiveKind>> = {};
  for (const scope of scopes) {
    const d = data[scope];
    if (d) liveKinds[scope] = BrandKitUtils.liveKind(d.assetSets, d.currentImages);
  }
  const drafts = BrandKitUtils.kitDrafts(scopes, data);
  const ticked = BrandKitUtils.effectiveTicks(chosenTicks, scopes, liveKinds, drafts.map((d) => d.scope));
  const quick = BrandKitUtils.quickScopes(kit, scopes, data);
  const disabled = !isAppActive;

  const generate = (req: FactoryBrandKitGenerateReq) => {
    generateMutation.mutate(req, {
      onSuccess: () => notifications.show({ title: C.generatedTitle, message: C.generatedMsg, color: 'green' }),
      onError: (err) =>
        BrandKitUtils.isStillWorking(err)
          ? notifications.show({ title: C.generateSlowTitle, message: C.generateSlowMsg, color: 'blue' })
          : notifications.show({
              title: 'Error',
              message: err instanceof ApiError ? BrandKitUtils.refusalMessage(err, BRAND_KIT_GENERATE_ERRORS) : err.message,
              color: 'red',
            }),
    });
  };

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed" maw={760}>
        {C.intro.replace('{platforms}', BrandKitUtils.tvLabel(tv))}
      </Text>

      {!isAppActive && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          Activate the app first to change its Brand Kit.
        </Alert>
      )}

      {quick.map((scope) => {
        const label = BrandKitUtils.scopeLabel(scope, tv);
        return (
          <Alert key={scope} icon={<IconSparkles size={16} />} color="blue" title={C.quickTitle.replace('{platform}', label)} data-testid="brand-kit-quick">
            <Group gap="sm">
              <Text size="sm">{C.quickBody}</Text>
              <Button
                size="xs"
                onClick={() => generate(BrandKitUtils.kitStoredRequest([scope]))}
                loading={generateMutation.isPending}
                disabled={disabled}
              >
                {C.quickAction.replace('{platform}', label)}
              </Button>
            </Group>
          </Alert>
        );
      })}

      <BrandKitDrafts appCfgId={appCfgId} drafts={drafts} data={data} tv={tv} disabled={disabled} />

      <BrandKitForm
        // A new stored kit (or a first seed) is a new form: its picks were just saved.
        key={kit?.brandKitId ?? (seed ? `seed:${seed.logoUrl}` : 'new')}
        kit={kit}
        seed={seed}
        scopes={scopes}
        tv={tv}
        liveKinds={liveKinds}
        ticked={ticked}
        onTickedChange={setChosenTicks}
        defaultAppName={BrandKitUtils.defaultAppName(app)}
        hasDrafts={drafts.length > 0}
        disabled={disabled}
        generating={generateMutation.isPending}
        onGenerate={generate}
      />
    </Stack>
  );
}
